/**
 * Loading State Manager for ML Models
 * Provides user feedback during model loading
 */

export const ModelLoadingState = {
  IDLE: 'idle',
  CHECKING_CACHE: 'checking_cache',
  REQUESTING_FROM_NATIVE: 'requesting_from_native',
  LOADING_MODEL: 'loading_model',
  SAVING_TO_CACHE: 'saving_to_cache',
  READY: 'ready',
  ERROR: 'error'
};

class LoadingStateManager {
  constructor() {
    this.currentState = ModelLoadingState.IDLE;
    this.listeners = [];
    this.modelStates = {
      movenet: ModelLoadingState.IDLE,
      blazeposeDetector: ModelLoadingState.IDLE,
      blazeposeLandmark: ModelLoadingState.IDLE
    };
    this.loadingProgress = {
      current: 0,
      total: 3,
      message: ''
    };
  }

  /**
   * Subscribe to state changes
   */
  subscribe(listener) {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  /**
   * Notify all listeners of state change
   */
  notifyListeners() {
    this.listeners.forEach(listener => {
      listener({
        state: this.currentState,
        modelStates: this.modelStates,
        progress: this.loadingProgress
      });
    });
  }

  /**
   * Update overall loading state
   */
  setState(state) {
    this.currentState = state;
    this.notifyListeners();
  }

  /**
   * Update specific model loading state
   */
  setModelState(modelName, state) {
    this.modelStates[modelName] = state;
    
    // Update overall state based on individual model states
    const states = Object.values(this.modelStates);
    if (states.every(s => s === ModelLoadingState.READY)) {
      this.currentState = ModelLoadingState.READY;
    } else if (states.some(s => s === ModelLoadingState.ERROR)) {
      this.currentState = ModelLoadingState.ERROR;
    } else if (states.some(s => s === ModelLoadingState.REQUESTING_FROM_NATIVE)) {
      this.currentState = ModelLoadingState.REQUESTING_FROM_NATIVE;
    } else if (states.some(s => s === ModelLoadingState.LOADING_MODEL)) {
      this.currentState = ModelLoadingState.LOADING_MODEL;
    }
    
    this.notifyListeners();
  }

  /**
   * Update loading progress
   */
  updateProgress(current, total, message) {
    this.loadingProgress = { current, total, message };
    this.notifyListeners();
  }

  /**
   * Reset all states
   */
  reset() {
    this.currentState = ModelLoadingState.IDLE;
    this.modelStates = {
      movenet: ModelLoadingState.IDLE,
      blazeposeDetector: ModelLoadingState.IDLE,
      blazeposeLandmark: ModelLoadingState.IDLE
    };
    this.loadingProgress = {
      current: 0,
      total: 3,
      message: ''
    };
    this.notifyListeners();
  }

  /**
   * Get loading message for current state
   */
  getLoadingMessage() {
    switch (this.currentState) {
      case ModelLoadingState.CHECKING_CACHE:
        return 'Checking cached models...';
      case ModelLoadingState.REQUESTING_FROM_NATIVE:
        return 'Requesting models from app...';
      case ModelLoadingState.LOADING_MODEL:
        return `Loading models (${this.loadingProgress.current}/${this.loadingProgress.total})...`;
      case ModelLoadingState.SAVING_TO_CACHE:
        return 'Saving models for faster loading next time...';
      case ModelLoadingState.READY:
        return 'Models ready!';
      case ModelLoadingState.ERROR:
        return 'Error loading models. Please try again.';
      default:
        return '';
    }
  }
}

// Singleton instance
export const loadingStateManager = new LoadingStateManager();
